import {
  FC,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ChangeEvent,
} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import { AxiosError } from "axios";
import { format } from "date-fns";
import { UserFormTextField } from "@/components/UserFormTextField/UserFormTextField";
import { useSnackbar } from "@/components/Snackbar";
import { createSegment, updateSegment } from "@/apis/bacchus/segment";
import { Segment, isValidationError } from "@/apis/bacchus/types";

export type SegmentDialogMode = "create" | "edit";

const DEFAULT_CONFIG: Record<
  SegmentDialogMode,
  { title: string; buttonText: string }
> = {
  create: { title: "Save Segment", buttonText: "Save Segment" },
  edit: { title: "Edit Segment", buttonText: "Save" },
};

interface SegmentDialogProps {
  open: boolean;
  mode: SegmentDialogMode;
  segment?: Segment | null;
  title?: string;
  /** JSON string of OpportunityGeneratorFilter to persist */
  filtersJson?: string;
  /** Pre-populated name shown in create mode (ignored in edit mode). */
  initialName?: string;
  onClose: () => void;
  onSave: (segmentId?: string) => void;
}

function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return "—";
  return format(new Date(dateString), "MMM d, yyyy");
}

export const SegmentDialog: FC<SegmentDialogProps> = ({
  open,
  mode,
  segment,
  title,
  filtersJson,
  initialName,
  onClose,
  onSave,
}) => {
  const snackbar = useSnackbar();
  const isEditMode = mode === "edit";

  // Form state
  const [name, setName] = useState("");

  // Validation state
  const [isNameInError, setIsNameInError] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  // API state
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      if (isEditMode && segment) {
        setName(segment.name || "");
      } else {
        setName(initialName ?? "");
      }
      setIsNameInError(false);
      setNameErrorMessage("");
      setNameTouched(false);
    }
  }, [open, isEditMode, segment, initialName]);

  // Validate name (only show error if field was touched)
  useEffect(() => {
    if (open && nameTouched) {
      if (!name.trim()) {
        setIsNameInError(true);
        setNameErrorMessage("Name is required");
      } else {
        setIsNameInError(false);
        setNameErrorMessage("");
      }
    }
  }, [name, open, nameTouched]);

  // Check if form has been modified (only relevant for edit mode)
  const hasFormBeenModified = useMemo(() => {
    if (!isEditMode || !segment) return true; // Always "modified" in create mode
    return name.trim() !== (segment.name || "").trim();
  }, [isEditMode, name, segment]);

  // Save button disabled state
  const isSaveDisabled = isSaving || isNameInError || !hasFormBeenModified;

  const handleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setName(e.target.value);
      setNameTouched(true);
    },
    []
  );

  const handleSave = useCallback(async () => {
    // Mark field as touched to show validation errors
    setNameTouched(true);

    // Wait for validation to run
    if (!name.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      if (isEditMode) {
        if (!segment?.id) return;
        await updateSegment(segment.id, {
          name: name.trim(),
        });
        snackbar.showSuccess("Segment updated successfully");
        onSave();
      } else {
        const newSegment = await createSegment({
          name: name.trim(),
          metadata: { filters: filtersJson ?? "{}" },
        });
        snackbar.showSuccess("Segment created successfully");
        onSave(newSegment.id);
      }
    } catch (err) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} segment:`,
        err
      );

      // Handle validation errors (e.g., duplicate name)
      if (err instanceof AxiosError && err.response?.data) {
        const errorData = err.response.data;
        if (isValidationError(errorData)) {
          const nameErrors = errorData.errors.name;
          if (nameErrors && nameErrors.length > 0) {
            setIsNameInError(true);
            setNameErrorMessage(
              nameErrors[0] === "has already been taken"
                ? "A segment with this name already exists"
                : nameErrors[0]
            );
            return;
          }
        }
      }

      snackbar.showError(
        `Failed to ${isEditMode ? "update" : "create"} segment. Please try again.`
      );
    } finally {
      setIsSaving(false);
    }
  }, [isEditMode, segment?.id, name, filtersJson, snackbar, onSave]);

  const resetForm = useCallback(() => {
    setName(isEditMode ? "" : (initialName ?? ""));
    setIsNameInError(false);
    setNameErrorMessage("");
    setNameTouched(false);
  }, [isEditMode, initialName]);

  // Dynamic content based on mode
  const dialogTitle = title ?? DEFAULT_CONFIG[mode].title;
  const saveButtonText = DEFAULT_CONFIG[mode].buttonText;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      id="segment-dialog"
      slotProps={{
        transition: {
          onExited: resetForm,
        },
      }}
    >
      <DialogTitle
        variant="h3"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {dialogTitle}
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          id="segment-dialog-close-btn"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          <UserFormTextField
            id="segment-name-input"
            label="Name"
            value={name}
            onChange={handleNameChange}
            error={isNameInError}
            errorMessage={nameErrorMessage}
            autoFocus={open}
            maxLength={200}
          />
          {/* Read-only metadata - only in edit mode */}
          {isEditMode && segment && (
            <Stack direction="row" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {formatDate(segment.inserted_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last Modified
                </Typography>
                <Typography variant="body2">
                  {formatDate(segment.updated_at)}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          id="segment-dialog-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaveDisabled}
          id="segment-dialog-save-btn"
          data-testid="segment-dialog-save-btn"
        >
          {isSaving ? "Saving..." : saveButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
